"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useProfileSections = void 0;
const react_1 = require("react");
const get_1 = __importDefault(require("lodash/get"));
const constants_1 = require("../constants");
const utils_1 = require("../utils");
const messages_1 = __importDefault(require("../messages"));
/**
 * hook that manages:
 * which sections should be rendered on the profile
 * which navigation items should be rendered
 */
const useProfileSections = ({ config, provider, locations, careTeamProviders, altProviders, shouldRenderHighlightsNav, renderableSections }) => {
    var _a, _b;
    const rendered = (0, react_1.useMemo)(() => {
        const highlightsSection = Boolean(shouldRenderHighlightsNav);
        const videoSection = Boolean(provider.video_url);
        const locationsSection = Boolean(locations === null || locations === void 0 ? void 0 : locations.length);
        const aboutSection = (0, utils_1.showAboutSection)(config, provider);
        const appointmentsSection = (0, utils_1.showAppointmentsSection)(config, provider);
        const clinicalInterestsSection = (0, utils_1.showClinicalInterestsSection)(provider);
        const expertiseSection = (0, utils_1.showExpertiseSection)(provider);
        const insuranceSection = (0, utils_1.showInsuranceSection)(config, provider);
        const reviewsSection = (0, utils_1.showReviewSection)(provider);
        const careTeamSection = (0, utils_1.showCareTeamSection)(config, careTeamProviders);
        const altProvidersSection = (0, utils_1.showAltProvidersSection)(config, altProviders);
        return {
            highlightsSection,
            aboutSection,
            appointmentsSection,
            videoSection,
            locationsSection,
            clinicalInterestsSection,
            expertiseSection,
            insuranceSection,
            reviewsSection,
            careTeamSection,
            altProvidersSection
        };
    }, [
        config,
        provider,
        locations,
        altProviders,
        careTeamProviders,
        shouldRenderHighlightsNav
    ]);
    const navItems = (0, react_1.useMemo)(() => {
        const getNavItem = (sectionId) => {
            var _a, _b, _c;
            switch (sectionId) {
                case constants_1.PROFILE_SECTION_IDS.HIGHLIGHTS:
                    return {
                        id: constants_1.PROFILE_SECTION_IDS.HIGHLIGHTS,
                        href: `#${constants_1.PROFILE_SECTION_IDS.HIGHLIGHTS}`,
                        name: 'Highlights',
                        label: messages_1.default.highlights,
                        rendered: rendered.highlightsSection
                    };
                case constants_1.PROFILE_SECTION_IDS.ABOUT:
                    return {
                        id: constants_1.PROFILE_SECTION_IDS.ABOUT,
                        name: 'About',
                        href: `#${constants_1.PROFILE_SECTION_IDS.ABOUT}`,
                        label: messages_1.default.aboutNavTitle,
                        rendered: rendered.aboutSection
                    };
                case constants_1.PROFILE_SECTION_IDS.APPOINTMENTS:
                    return {
                        id: constants_1.PROFILE_SECTION_IDS.APPOINTMENTS,
                        href: `#${constants_1.PROFILE_SECTION_IDS.APPOINTMENTS}`,
                        name: 'Appointments',
                        label: messages_1.default.appointmentsNavTitle,
                        rendered: rendered.appointmentsSection
                    };
                case constants_1.PROFILE_SECTION_IDS.VIDEOS:
                    return {
                        id: constants_1.PROFILE_SECTION_IDS.VIDEOS,
                        href: `#${constants_1.PROFILE_SECTION_IDS.VIDEOS}`,
                        name: 'Videos',
                        label: messages_1.default.videosNavTitle,
                        rendered: rendered.videoSection
                    };
                case constants_1.PROFILE_SECTION_IDS.LOCATIONS:
                    return {
                        id: constants_1.PROFILE_SECTION_IDS.LOCATIONS,
                        href: `#${constants_1.PROFILE_SECTION_IDS.LOCATIONS}`,
                        name: 'Locations',
                        label: messages_1.default.locationsNavTitle,
                        rendered: rendered.locationsSection
                    };
                case constants_1.PROFILE_SECTION_IDS.CLINICAL_INTERESTS:
                    return {
                        id: constants_1.PROFILE_SECTION_IDS.CLINICAL_INTERESTS,
                        href: `#${constants_1.PROFILE_SECTION_IDS.CLINICAL_INTERESTS}`,
                        name: 'Clinical Interests',
                        label: messages_1.default.clinicalInterestsNavTitle,
                        rendered: rendered.clinicalInterestsSection
                    };
                case constants_1.PROFILE_SECTION_IDS.EXPERTISE:
                    return {
                        id: constants_1.PROFILE_SECTION_IDS.EXPERTISE,
                        name: 'Expertise',
                        href: `#${constants_1.PROFILE_SECTION_IDS.EXPERTISE}`,
                        label: messages_1.default.expertiseNavTitle,
                        rendered: rendered.expertiseSection
                    };
                case constants_1.PROFILE_SECTION_IDS.INSURANCE_ACCEPTED:
                    return {
                        id: constants_1.PROFILE_SECTION_IDS.INSURANCE_ACCEPTED,
                        href: `#${constants_1.PROFILE_SECTION_IDS.INSURANCE_ACCEPTED}`,
                        name: 'Insurance',
                        label: messages_1.default.insuranceNavTitle,
                        rendered: rendered.insuranceSection
                    };
                case constants_1.PROFILE_SECTION_IDS.REVIEWS:
                    return {
                        id: constants_1.PROFILE_SECTION_IDS.REVIEWS,
                        href: `#${constants_1.PROFILE_SECTION_IDS.REVIEWS}`,
                        name: 'Reviews',
                        label: messages_1.default.reviewsNavTitle,
                        rendered: rendered.reviewsSection
                    };
                case constants_1.PROFILE_SECTION_IDS.CARE_TEAM:
                    return {
                        id: constants_1.PROFILE_SECTION_IDS.CARE_TEAM,
                        href: `#${constants_1.PROFILE_SECTION_IDS.CARE_TEAM}`,
                        name: 'Care Team',
                        label: messages_1.default.careTeamNavTitle,
                        rendered: rendered.careTeamSection
                    };
                case constants_1.PROFILE_SECTION_IDS.SIMILAR_PROVIDERS:
                    return {
                        id: constants_1.PROFILE_SECTION_IDS.SIMILAR_PROVIDERS,
                        name: 'Similar Providers',
                        href: `#${constants_1.PROFILE_SECTION_IDS.SIMILAR_PROVIDERS}`,
                        label: messages_1.default.altProvidersNavTitle,
                        rendered: rendered.altProvidersSection
                    };
                default: {
                    // must be a custom xp field
                    const customXPFieldConfig = (_c = (_b = (_a = config.provider_v9) === null || _a === void 0 ? void 0 : _a.sections) === null || _b === void 0 ? void 0 : _b.custom) === null || _c === void 0 ? void 0 : _c.find((xpFieldConfig) => xpFieldConfig.id === sectionId);
                    if (customXPFieldConfig) {
                        // if we have an XP field config, check to see if the section actually rendered (has provider data)
                        const rendered = Boolean((0, get_1.default)(provider, customXPFieldConfig.field, null));
                        if (rendered) {
                            return {
                                id: customXPFieldConfig.id,
                                name: customXPFieldConfig.title,
                                href: `#${customXPFieldConfig.id}`,
                                label: customXPFieldConfig.title,
                                rendered: rendered
                            };
                        }
                    }
                }
            }
            return null;
        };
        return renderableSections
            .map((sectionId) => getNavItem(sectionId))
            .filter(Boolean);
    }, [
        renderableSections,
        rendered,
        (_b = (_a = config.provider_v9) === null || _a === void 0 ? void 0 : _a.sections) === null || _b === void 0 ? void 0 : _b.custom,
        provider
    ]);
    return {
        navItems,
        rendered
    };
};
exports.useProfileSections = useProfileSections;
//# sourceMappingURL=useProfileSections.js.map